import { Injectable, InjectionToken, inject } from "@angular/core";
import { Route, UrlMatchResult, UrlSegment, UrlSegmentGroup } from "@angular/router";

import { BehaviorSubject, distinctUntilChanged, map, Observable } from "rxjs";

import { SessionStorageService } from "#shared/services/session-storage.service";

import { TranslateHelpers } from "./helpers";
import { Language } from "./language";
import { Translation, TranslationPath, TranslationPathArray } from "./model";
import { i18n_de } from "./translations/de";

const allTranslations: Record<Language, Translation> = {
  [Language.DE]: i18n_de,
};

export const TRANSLATION_DEFAULT_LANGUAGE = new InjectionToken<Language>("TRANSLATION_DEFAULT_LANGUAGE");

const LOCAL_STORAGE_LANGUAGE_KEY = "last_selected_language";

@Injectable({ providedIn: "root" })
export class TranslateService {
  private readonly storageService = inject(SessionStorageService);
  private readonly defaultLanguage = inject<Language>(TRANSLATION_DEFAULT_LANGUAGE, { optional: true });

  private readonly state$ = new BehaviorSubject<{ translations: Translation; currentLanguage: Language }>({
    translations: i18n_de,
    currentLanguage: Language.DE,
  });

  get currentLang() {
    return this.state$.getValue().currentLanguage;
  }

  readonly currentLanguage$ = this.state$.pipe(
    map((s) => s.currentLanguage),
    distinctUntilChanged()
  );

  readonly translations$ = this.state$.pipe(map((s) => s.translations));

  constructor() {
    const defaultLanguage = this.defaultLanguage;

    const restoredLanguage = this.storageService.getItem<string>(LOCAL_STORAGE_LANGUAGE_KEY) || this.getBrowserLang();
    this.use(isValidLanguage(restoredLanguage) ? restoredLanguage : (defaultLanguage ?? Language.DE));
  }

  instant(key: TranslationPath | TranslationPathArray, params?: any): string {
    const { translations } = this.state$.getValue();
    return TranslateHelpers.interpolate(TranslateHelpers.getValue(translations, key), params) ?? joinIfNecessary(key, ".");
  }

  stream<T>(key: string, params?: any, fallback?: string | string[]): Observable<T>;
  stream(key: TranslationPath | TranslationPathArray, params: any, fallback: string | string[]): Observable<string>;
  stream(key: TranslationPath | TranslationPathArray, params?: any, fallback?: string | string[]): Observable<string | undefined>;
  stream(key: string | string[], params?: any, fallback?: string | string[]): Observable<string | undefined> {
    return this.state$.pipe(
      map(({ translations }) => TranslateHelpers.interpolate(TranslateHelpers.getValue(translations, key), params)) ??
        joinIfNecessary(fallback, ".")
    );
  }

  use(lang: string) {
    if (lang === this.state$.getValue().currentLanguage) return;
    if (!isValidLanguage(lang)) return;
    this.state$.next({
      translations: allTranslations[lang],
      currentLanguage: lang,
    });
    this.storageService.setItem(LOCAL_STORAGE_LANGUAGE_KEY, lang);
  }

  private getBrowserLang(): string | undefined {
    if (typeof window === "undefined" || typeof window.navigator === "undefined") {
      return undefined;
    }

    const navigator: any = window.navigator;
    let browserLang: any = navigator.languages ? navigator.languages[0] : null;
    browserLang = browserLang || navigator.language || navigator.browserLanguage || navigator.userLanguage;

    if (typeof browserLang === "undefined") return;

    if (browserLang.indexOf("-") !== -1) {
      browserLang = browserLang.split("-")[0];
    }

    if (browserLang.indexOf("_") !== -1) {
      browserLang = browserLang.split("_")[0];
    }

    return browserLang;
  }
}

export function languageMatcher(segments: UrlSegment[], group: UrlSegmentGroup, route: Route): UrlMatchResult {
  const lang = segments[0]?.path;
  return lang && isValidLanguage(lang)
    ? { consumed: [segments[0]], posParams: { lang: new UrlSegment(segments[0].path, { lang }) } }
    : (null as any);
}

export function isValidLanguage(lang: string | undefined): lang is Language {
  return lang != null && allTranslations[lang] != null;
}

function joinIfNecessary(val: string | string[], divider: string): string;
function joinIfNecessary(val: string | string[] | undefined, divider: string): string | undefined;
function joinIfNecessary(val: string | string[] | undefined, divider: string): string | undefined {
  return Array.isArray(val) ? val.join(divider) : val;
}
